import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  PerformanceIndicator,
  PerformanceData
} from "../../components/dashboard/performanceIndicators/performanceIndicator";
import { useStore } from "../../models";
import styles from "./IndicatorsContainer.css";
import { config } from "@fidget/common/config";
import { L10n } from "@fidget/common/i18n";

const AverageResponseTimeIndicator = observer(() => {
  const { currentUserStore } = useStore();
  const {
    averageResponseTimeSlaEnabled,
    averageResponseTimeSlaRangeLowerBound,
    averageResponseTimeSlaRangeUpperBound
  } = currentUserStore.orgConfig!;

  const avgResponseRange =
    averageResponseTimeSlaEnabled &&
      averageResponseTimeSlaRangeLowerBound !== undefined &&
      averageResponseTimeSlaRangeUpperBound !== undefined
      ? {
        belowBound: averageResponseTimeSlaRangeLowerBound,
        aboveBound: averageResponseTimeSlaRangeUpperBound
      }
      : undefined;

  const averageResponseTimeData: PerformanceData = {
    value: currentUserStore.avgResponseTimeMillis,
    ranges: avgResponseRange
  };

  const millisToMinutes = (millis: number): number => millis / 1000 / 60;

  const pad = (value: number): string => (value < 10 ? "0" : "") + value;

  const formatMillis = (millis: number): string => {
    const result = {
      h: Math.floor(millis / 1000 / 60 / 60),
      m: Math.floor(millis / 1000 / 60 % 60),
      s: Math.floor(millis / 1000 % 60),
    };
    return [
      result.h > 0 ? `${result.h}h` : "",
      result.h > 0 ? `${pad(result.m)}m` : `${result.m}m`,
      result.h === 0 ? `${pad(result.s)}s` : "",
    ].filter(Boolean).join(" ");
  };

  const formatAvgResponse = (data: PerformanceData): PerformanceData => {
    const value = millisToMinutes(data.value);
    const formattedValue = formatMillis(data.value);
    const ranges =
      data.ranges !== undefined
        ? {
          belowBound: millisToMinutes(data.ranges.belowBound),
          aboveBound: millisToMinutes(data.ranges.aboveBound)
        }
        : undefined;

    return { value, formattedValue, ranges };
  };

  useEffect(() => {
    currentUserStore.reloadAverageResponseTime();

    const id = setInterval(() => {
      currentUserStore.reloadAverageResponseTime();
    }, config.services.times.avgResponseReloadIntervalInMillis);

    return () => {
      clearInterval(id);
    };
  }, []);

  return (
    <PerformanceIndicator
      iconClassName="icon-first-message-time"
      tooltipText={<L10n id={"reports.averageResponseTime.tooltip"} />}
      data={formatAvgResponse(averageResponseTimeData)}
      decimal
      maxWidth="250%"
      minWidth="250%"
    />
  );
});

const WaitingConversationsIndicator = observer(() => {
  const { inboxStore, currentUserStore, tagGroupsStore } = useStore();
  const {
    waitingConversationsSlaEnabled,
    waitingConversationsSlaRangeUpperBound,
    waitingConversationsSlaRangeLowerBound,
  } = currentUserStore.orgConfig!;

  const waitingRange =
    waitingConversationsSlaEnabled &&
      waitingConversationsSlaRangeLowerBound !== undefined &&
      waitingConversationsSlaRangeUpperBound !== undefined
      ? {
        belowBound: waitingConversationsSlaRangeLowerBound,
        aboveBound: waitingConversationsSlaRangeUpperBound
      }
      : undefined;

  const waitingData: PerformanceData = {
    value: inboxStore.unassignedCounters.count,
    ranges: waitingRange
  };

  return (
    <PerformanceIndicator
      iconClassName="icon-person-bold"
      tooltipText={
      <>
        <L10n id={"reports.waitingQueue.tooltip"} />
        <table>
          <tbody>
          {
            Array
              .from(inboxStore.unassignedCounters.countByTagGroup.entries())
              .sort((a, b) => b[1] - a[1])
              .filter(([k, v]) => v > 0)
              .map(([k, v]) =>
                <tr>
                  <td style={{textAlign: "right"}}>{v}{"\u00A0-\u00A0"}</td>
                  <td style={{textAlign: "left"}}>{tagGroupsStore.getById(k)?.name}</td>
                </tr>
              )
          }
          </tbody>
        </table>
      </>
      }
      autoHideTooltip={false}
      data={waitingData}
      maxWidth="300%"
      minWidth="100%"
    />
  );
});

const OpenedConversationsIndicator = observer(() => {
  const { inboxStore, currentUserStore } = useStore();
  const {
    openedConversationsSlaEnabled,
    openedConversationsSlaRangeLowerBound,
    openedConversationsSlaRangeUpperBound,
  } = currentUserStore.orgConfig!;

  const openedRange =
    openedConversationsSlaEnabled &&
      openedConversationsSlaRangeLowerBound !== undefined &&
      openedConversationsSlaRangeUpperBound !== undefined
      ? {
        belowBound: openedConversationsSlaRangeLowerBound,
        aboveBound: openedConversationsSlaRangeUpperBound
      }
      : undefined;

  const yoursData: PerformanceData = {
    value: inboxStore.yoursLength,
    ranges: openedRange
  };

  return (
    <PerformanceIndicator
      iconClassName="icon-active-conversations"
      tooltipText={<L10n id={"reports.yoursConversations.tooltip"} />}
      data={yoursData}
      maxWidth="150%"
    />
  );
});

export const IndicatorsContainer = observer(() => {
  const { currentUserStore } = useStore();
  const {
    openedConversationsMetricEnabled,
    waitingConversationsMetricEnabled,
    averageResponseTimeMetricEnabled,
  } = currentUserStore.orgConfig!;

  return (
    <div className={styles.container}>
      {averageResponseTimeMetricEnabled && <AverageResponseTimeIndicator />}
      {waitingConversationsMetricEnabled && <WaitingConversationsIndicator />}
      {openedConversationsMetricEnabled && <OpenedConversationsIndicator />}
    </div>
  );
});
